import { createTheme, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

// The backoffice palette is the public site's: near-black ink, amber accent,
// warm paper. Keeping them in one place is what lets every screen be restyled
// by editing this file instead of the inline `style={{...}}` objects that used
// to be scattered through the containers.
export const INK = '#191716';
export const AMBER = '#ffcb77';
export const PAPER = '#f7f5f0';
export const LIGHT = '#E0E2DB';

// The wordmark face, applied where the brand is written out. Not a Typography
// variant on purpose — MUI v5 warns on unregistered variants, and one `sx`
// reference is clearer than the registration ceremony.
export const BRAND_FONT = `'Oleo Script Swash Caps', cursive`;

// One hook, used everywhere a layout has to collapse: below `md` the app is
// driven by a thumb on a phone, so grids become single-column, tables become
// card lists and dialogs go full-screen.
export const useIsMobile = () => useMediaQuery(useTheme().breakpoints.down('md'));

// True only on genuinely narrow phones, where even two side-by-side buttons
// stop fitting.
export const useIsNarrow = () => useMediaQuery(useTheme().breakpoints.down('sm'));

const theme = createTheme({
    palette: {
        mode: 'light',
        primary: { main: INK, contrastText: LIGHT },
        secondary: { main: '#a8702a', contrastText: '#fff' },
        background: { default: PAPER, paper: '#fff' },
        text: { primary: INK, secondary: '#5c5854' },
        divider: 'rgba(25, 23, 22, 0.12)',
    },
    shape: { borderRadius: 10 },
    typography: {
        fontFamily: `'Noto Sans Lao Looped', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`,
        h1: { fontSize: '1.6rem', fontWeight: 700 },
        h2: { fontSize: '1.3rem', fontWeight: 700 },
        button: { textTransform: 'none', fontWeight: 600 },
    },
    components: {
        // 16px is the smallest font size iOS Safari will not zoom into when a
        // field is focused. Anything smaller makes every tap on a text field
        // jump the viewport, which is the single worst thing a mobile form can do.
        MuiInputBase: {
            styleOverrides: {
                input: { '@media (max-width: 899.95px)': { fontSize: 16 } },
            },
        },
        MuiButton: {
            defaultProps: { disableElevation: true },
            styleOverrides: {
                // 44px is the minimum comfortable touch target.
                root: { minHeight: 44 },
                sizeSmall: { minHeight: 36 },
            },
        },
        MuiIconButton: {
            styleOverrides: { root: { minWidth: 44, minHeight: 44 } },
        },
        MuiTab: {
            styleOverrides: { root: { textTransform: 'none', fontWeight: 600, minHeight: 48 } },
        },
        MuiPaper: {
            styleOverrides: { rounded: { borderRadius: 12 } },
        },
        MuiTextField: {
            defaultProps: { size: 'small' },
        },
        MuiDialogContent: {
            styleOverrides: { root: { paddingTop: 8 } },
        },
    },
});

export default theme;
